import React from 'react';

const Shape3D = ({ data }) => {
  if (!data) return null;

  const { shape, dimensions = {}, labels = {}, title = '' } = data;
  const svgW = 300, svgH = 300;
  const cx = svgW / 2, cy = svgH / 2;

  // Isometric projection helpers
  const isoX = (x, y) => cx + (x - y) * 0.866 * 18;
  const isoY = (x, y, z) => cy - z * 18 + (x + y) * 0.5 * 18;

  // Label rendering helper
  const renderLabel = (x, y, text, anchor = 'middle', color = '#333') => (
    <text x={x} y={y} textAnchor={anchor} dominantBaseline="middle"
      className="diagram-dim-label" fill={color}>{text}</text>
  );

  // Dashed line for hidden edges
  const dashProps = { stroke: '#999', strokeWidth: '1', strokeDasharray: '4,3' };
  const solidProps = { stroke: '#1976d2', strokeWidth: '2' };
  const fillFront = 'rgba(33, 150, 243, 0.12)';
  const fillTop = 'rgba(33, 150, 243, 0.06)';
  const fillSide = 'rgba(33, 150, 243, 0.18)';

  const renderShape = () => {
    switch (shape) {
      case 'rectangular_prism':
      case 'cube': {
        const l = dimensions.length || 5;
        const w = dimensions.width || (shape === 'cube' ? l : 3);
        const h = dimensions.height || (shape === 'cube' ? l : 4);
        // 8 corners
        const p = {
          fbl: [isoX(0, 0), isoY(0, 0, 0)],       // front-bottom-left
          fbr: [isoX(l, 0), isoY(l, 0, 0)],       // front-bottom-right
          bbl: [isoX(0, w), isoY(0, w, 0)],       // back-bottom-left
          bbr: [isoX(l, w), isoY(l, w, 0)],       // back-bottom-right
          ftl: [isoX(0, 0), isoY(0, 0, h)],       // front-top-left
          ftr: [isoX(l, 0), isoY(l, 0, h)],       // front-top-right
          btl: [isoX(0, w), isoY(0, w, h)],       // back-top-left
          btr: [isoX(l, w), isoY(l, w, h)],       // back-top-right
        };
        return (
          <g>
            {/* Hidden edges (dashed) */}
            <line x1={p.bbl[0]} y1={p.bbl[1]} x2={p.bbr[0]} y2={p.bbr[1]} {...dashProps} />
            <line x1={p.bbl[0]} y1={p.bbl[1]} x2={p.fbl[0]} y2={p.fbl[1]} {...dashProps} />
            <line x1={p.bbl[0]} y1={p.bbl[1]} x2={p.btl[0]} y2={p.btl[1]} {...dashProps} />
            {/* Front face */}
            <polygon points={`${p.fbl.join(',')} ${p.fbr.join(',')} ${p.ftr.join(',')} ${p.ftl.join(',')}`}
              fill={fillFront} {...solidProps} />
            {/* Top face */}
            <polygon points={`${p.ftl.join(',')} ${p.ftr.join(',')} ${p.btr.join(',')} ${p.btl.join(',')}`}
              fill={fillTop} {...solidProps} />
            {/* Right face */}
            <polygon points={`${p.fbr.join(',')} ${p.bbr.join(',')} ${p.btr.join(',')} ${p.ftr.join(',')}`}
              fill={fillSide} {...solidProps} />
            {/* Labels */}
            {labels.length && renderLabel((p.fbl[0] + p.fbr[0]) / 2, (p.fbl[1] + p.fbr[1]) / 2 + 18, labels.length)}
            {labels.width && renderLabel((p.fbr[0] + p.bbr[0]) / 2 + 14, (p.fbr[1] + p.bbr[1]) / 2 + 10, labels.width)}
            {labels.height && renderLabel(p.fbl[0] - 16, (p.fbl[1] + p.ftl[1]) / 2, labels.height)}
          </g>
        );
      }

      case 'cylinder': {
        const r = (dimensions.radius || 3) * 16;
        const h = (dimensions.height || 6) * 18;
        const topY = cy - h / 2;
        const botY = cy + h / 2;
        const ry = r * 0.35; // ellipse ratio
        return (
          <g>
            {/* Back edge of bottom ellipse (dashed) */}
            <ellipse cx={cx} cy={botY} rx={r} ry={ry} fill="none" {...dashProps} strokeDasharray="4,3" />
            {/* Side lines */}
            <line x1={cx - r} y1={topY} x2={cx - r} y2={botY} {...solidProps} />
            <line x1={cx + r} y1={topY} x2={cx + r} y2={botY} {...solidProps} />
            {/* Bottom ellipse front half */}
            <path d={`M${cx - r},${botY} A${r},${ry} 0 0,0 ${cx + r},${botY}`}
              fill={fillFront} {...solidProps} />
            {/* Top ellipse (full) */}
            <ellipse cx={cx} cy={topY} rx={r} ry={ry} fill={fillTop} {...solidProps} />
            {/* Height label */}
            {labels.height && renderLabel(cx + r + 20, (topY + botY) / 2, labels.height)}
            {/* Radius label */}
            {labels.radius && (
              <g>
                <line x1={cx} y1={topY} x2={cx + r} y2={topY} stroke="#e91e63" strokeWidth="1.5" strokeDasharray="4,3" />
                {renderLabel(cx + r / 2, topY - 12, labels.radius, 'middle', '#e91e63')}
              </g>
            )}
          </g>
        );
      }

      case 'cone': {
        const r = (dimensions.radius || 3) * 16;
        const h = (dimensions.height || 7) * 18;
        const botY = cy + h * 0.35;
        const topY = botY - h;
        const ry = r * 0.35;
        return (
          <g>
            {/* Base ellipse back (dashed) */}
            <path d={`M${cx - r},${botY} A${r},${ry} 0 0,1 ${cx + r},${botY}`}
              fill="none" {...dashProps} />
            {/* Side lines to apex */}
            <line x1={cx - r} y1={botY} x2={cx} y2={topY} {...solidProps} />
            <line x1={cx + r} y1={botY} x2={cx} y2={topY} {...solidProps} />
            {/* Base front half */}
            <path d={`M${cx - r},${botY} A${r},${ry} 0 0,0 ${cx + r},${botY}`}
              fill={fillFront} {...solidProps} />
            {/* Height dashed line */}
            <line x1={cx} y1={topY} x2={cx} y2={botY} {...dashProps} />
            {/* Apex point */}
            <circle cx={cx} cy={topY} r="3" fill="#1976d2" />
            {/* Labels */}
            {labels.height && renderLabel(cx + 16, (topY + botY) / 2, labels.height)}
            {labels.radius && renderLabel(cx + r / 2, botY + 18, labels.radius)}
            {labels.slantHeight && renderLabel(cx + r / 2 + 16, (topY + botY) / 2 - 10, labels.slantHeight)}
          </g>
        );
      }

      case 'sphere': {
        const r = (dimensions.radius || 4) * 20;
        return (
          <g>
            {/* Main circle */}
            <circle cx={cx} cy={cy} r={r} fill={fillTop} {...solidProps} />
            {/* Equator ellipse */}
            <ellipse cx={cx} cy={cy} rx={r} ry={r * 0.3} fill="none"
              stroke="#999" strokeWidth="1" strokeDasharray="4,3" />
            {/* Radius line */}
            <line x1={cx} y1={cy} x2={cx + r} y2={cy} stroke="#e91e63" strokeWidth="1.5" strokeDasharray="4,3" />
            <circle cx={cx} cy={cy} r="2.5" fill="#1976d2" />
            {labels.radius && renderLabel(cx + r / 2, cy - 12, labels.radius, 'middle', '#e91e63')}
          </g>
        );
      }

      case 'triangular_prism': {
        const b = dimensions.base || 5;
        const h = dimensions.height || 4;
        const d = dimensions.depth || 6;
        const th = dimensions.triangleHeight || (b * 0.866);
        // Front triangle
        const f1 = [cx - b * 8, cy + h * 6];
        const f2 = [cx + b * 8, cy + h * 6];
        const f3 = [cx, cy + h * 6 - th * 16];
        // Back triangle (offset for depth)
        const offX = d * 7, offY = -d * 4;
        const b1 = [f1[0] + offX, f1[1] + offY];
        const b2 = [f2[0] + offX, f2[1] + offY];
        const b3 = [f3[0] + offX, f3[1] + offY];
        return (
          <g>
            {/* Hidden edges */}
            <line x1={b1[0]} y1={b1[1]} x2={b2[0]} y2={b2[1]} {...dashProps} />
            <line x1={b1[0]} y1={b1[1]} x2={f1[0]} y2={f1[1]} {...dashProps} />
            <line x1={b1[0]} y1={b1[1]} x2={b3[0]} y2={b3[1]} {...dashProps} />
            {/* Front face */}
            <polygon points={`${f1.join(',')} ${f2.join(',')} ${f3.join(',')}`}
              fill={fillFront} {...solidProps} />
            {/* Top face */}
            <polygon points={`${f3.join(',')} ${f2.join(',')} ${b2.join(',')} ${b3.join(',')}`}
              fill={fillTop} {...solidProps} />
            {/* Right face */}
            <polygon points={`${f2.join(',')} ${b2.join(',')} ${b3.join(',')} ${f3.join(',')}`}
              fill={fillSide} {...solidProps} />
            {/* Bottom connecting edge */}
            <line x1={f2[0]} y1={f2[1]} x2={b2[0]} y2={b2[1]} {...solidProps} />
            {/* Labels */}
            {labels.base && renderLabel((f1[0] + f2[0]) / 2, f1[1] + 18, labels.base)}
            {labels.height && renderLabel(f1[0] - 16, (f1[1] + f3[1]) / 2, labels.height)}
            {labels.depth && renderLabel((f2[0] + b2[0]) / 2 + 14, (f2[1] + b2[1]) / 2, labels.depth)}
          </g>
        );
      }

      case 'pyramid': {
        const b = dimensions.base || 5;
        const h = dimensions.height || 7;
        const scale = 14;
        const half = b * scale / 2;
        const depthOff = b * scale * 0.35;
        const baseY = cy + h * scale * 0.3;
        // Base corners (isometric-ish)
        const fl = [cx - half, baseY];
        const fr = [cx + half, baseY];
        const bl = [cx - half * 0.4 + depthOff * 0.3, baseY - depthOff];
        const br = [cx + half * 0.4 + depthOff * 0.3, baseY - depthOff];
        const apex = [cx, baseY - h * scale];
        return (
          <g>
            {/* Hidden edges */}
            <line x1={bl[0]} y1={bl[1]} x2={br[0]} y2={br[1]} {...dashProps} />
            <line x1={bl[0]} y1={bl[1]} x2={fl[0]} y2={fl[1]} {...dashProps} />
            <line x1={bl[0]} y1={bl[1]} x2={apex[0]} y2={apex[1]} {...dashProps} />
            {/* Height line */}
            <line x1={cx} y1={baseY} x2={cx} y2={apex[1]} {...dashProps} />
            {/* Front face */}
            <polygon points={`${fl.join(',')} ${fr.join(',')} ${apex.join(',')}`}
              fill={fillFront} {...solidProps} />
            {/* Right face */}
            <polygon points={`${fr.join(',')} ${br.join(',')} ${apex.join(',')}`}
              fill={fillSide} {...solidProps} />
            {/* Base front edge */}
            <line x1={fl[0]} y1={fl[1]} x2={fr[0]} y2={fr[1]} {...solidProps} />
            <line x1={fr[0]} y1={fr[1]} x2={br[0]} y2={br[1]} {...solidProps} />
            {/* Apex */}
            <circle cx={apex[0]} cy={apex[1]} r="3" fill="#1976d2" />
            {/* Labels */}
            {labels.base && renderLabel((fl[0] + fr[0]) / 2, fl[1] + 18, labels.base)}
            {labels.height && renderLabel(cx + 16, (baseY + apex[1]) / 2, labels.height)}
            {labels.slantHeight && renderLabel((fr[0] + apex[0]) / 2 + 14, (fr[1] + apex[1]) / 2, labels.slantHeight)}
          </g>
        );
      }

      default:
        return (
          <text x={cx} y={cy} textAnchor="middle" className="diagram-label">
            3D shape: {shape}
          </text>
        );
    }
  };

  return (
    <div className="diagram-container">
      {title && <div className="diagram-title">{title}</div>}
      <svg viewBox={`0 0 ${svgW} ${svgH}`} className="shape3d-svg" role="img" aria-label={title || `3D ${shape}`}>
        {renderShape()}
      </svg>
    </div>
  );
};

export default Shape3D;
